import type {
  ContactZConnectionStatus,
  ContactZMessageType,
  ContactZOfficialTemplate,
  ContactZTemplateRequestComponent,
} from '../types';

export const SCRIPT_ASSET_TECHNICAL_MAX_BYTES = 100 * 1024 * 1024;

export const CONTACT_Z_MEDIA_LIMITS_BYTES: Record<Exclude<ContactZMessageType, 'text' | 'official_template'>, number> = {
  audio: 16 * 1024 * 1024,
  image: 5 * 1024 * 1024,
  video: 16 * 1024 * 1024,
  document: 100 * 1024 * 1024,
};

export const CONTACT_Z_CONNECTION_STATUS_SEVERITY: Record<
  ContactZConnectionStatus,
  'default' | 'success' | 'warning' | 'error'
> = {
  unknown: 'default',
  connecting: 'warning',
  connected: 'success',
  disconnected: 'error',
  error: 'error',
};

const CONTACT_Z_POLLABLE_CONNECTION_STATUSES = new Set<ContactZConnectionStatus>([
  'connecting',
  'disconnected',
  'error',
]);

const CONTACT_Z_CONNECTABLE_CONNECTION_STATUSES = new Set<ContactZConnectionStatus>([
  'unknown',
  'disconnected',
  'error',
]);

const CONTACT_Z_DISCONNECTABLE_CONNECTION_STATUSES = new Set<ContactZConnectionStatus>([
  'connecting',
  'connected',
]);

/**
 * Return whether the Contact Z message type requires a previously uploaded asset.
 */
export function contactZMessageTypeRequiresAsset(messageType: ContactZMessageType): boolean {
  return !['text', 'official_template'].includes(messageType);
}

/**
 * Return whether the message type accepts a caption template.
 */
export function contactZMessageTypeSupportsCaption(messageType: ContactZMessageType): boolean {
  return messageType === 'image' || messageType === 'video' || messageType === 'document';
}

/**
 * Return whether the message type accepts a text template in the primary field.
 */
export function contactZMessageTypeSupportsTextTemplate(messageType: ContactZMessageType): boolean {
  return messageType === 'text' || messageType === 'official_template';
}

export interface ContactZTemplateParameterSlot {
  id: string;
  componentType: 'header' | 'body' | 'button';
  parameterIndex: number;
  buttonIndex?: number;
  subType?: string;
  parameterType: string;
}

const CONTACT_Z_TEMPLATE_PLACEHOLDER_PATTERN = /{{\s*(\d+)\s*}}/g;

/** Describe every variable input required by one official Contact Z template. */
export function getContactZTemplateParameterSlots(
  template: ContactZOfficialTemplate | null,
): ContactZTemplateParameterSlot[] {
  if (!template) return [];
  const slots: ContactZTemplateParameterSlot[] = [];
  template.components.forEach((component) => {
    const componentType = String(component.type || '').toLowerCase();
    if (componentType === 'header' || componentType === 'body') {
      const placeholderCount = new Set(
        Array.from(JSON.stringify(component).matchAll(CONTACT_Z_TEMPLATE_PLACEHOLDER_PATTERN), (match) => match[1]),
      ).size;
      const format = String(component.format || 'text').toLowerCase();
      const requiredCount = componentType === 'header' && ['image', 'video', 'document'].includes(format)
        ? Math.max(placeholderCount, 1)
        : placeholderCount;
      for (let parameterIndex = 0; parameterIndex < requiredCount; parameterIndex += 1) {
        slots.push({
          id: `${componentType}:${parameterIndex}`,
          componentType,
          parameterIndex,
          parameterType: format === 'text' ? 'text' : format,
        });
      }
    }
    if (componentType === 'buttons') {
      (component.buttons || []).forEach((button, buttonIndex) => {
        const placeholderCount = new Set(
          Array.from(JSON.stringify(button).matchAll(CONTACT_Z_TEMPLATE_PLACEHOLDER_PATTERN), (match) => match[1]),
        ).size;
        for (let parameterIndex = 0; parameterIndex < placeholderCount; parameterIndex += 1) {
          slots.push({
            id: `button:${buttonIndex}:${parameterIndex}`,
            componentType: 'button',
            buttonIndex,
            parameterIndex,
            subType: String(button.type || 'url').toLowerCase(),
            parameterType: 'text',
          });
        }
      });
    }
  });
  return slots;
}

/** Read one editable value from the Meta-style request component tree. */
export function getContactZTemplateParameterValue(
  components: ContactZTemplateRequestComponent[],
  slot: ContactZTemplateParameterSlot,
): string {
  const component = components.find((item) => (
    item.type.toLowerCase() === slot.componentType
    && (slot.componentType !== 'button' || item.index === String(slot.buttonIndex))
  ));
  const parameter = component?.parameters[slot.parameterIndex];
  if (!parameter) return '';
  if (parameter.type === 'text') return parameter.text || '';
  const media = parameter[parameter.type as 'image' | 'video' | 'document'];
  return media?.link || '';
}

/** Immutably update one variable while preserving the upstream component shape. */
export function setContactZTemplateParameterValue(
  components: ContactZTemplateRequestComponent[],
  slot: ContactZTemplateParameterSlot,
  value: string,
): ContactZTemplateRequestComponent[] {
  const next = components.map((component) => ({
    ...component,
    parameters: component.parameters.map((parameter) => ({ ...parameter })),
  }));
  let component = next.find((item) => (
    item.type.toLowerCase() === slot.componentType
    && (slot.componentType !== 'button' || item.index === String(slot.buttonIndex))
  ));
  if (!component) {
    component = {
      type: slot.componentType,
      ...(slot.componentType === 'button'
        ? { index: String(slot.buttonIndex), subType: slot.subType || 'url' }
        : {}),
      parameters: [],
    };
    next.push(component);
  }
  while (component.parameters.length <= slot.parameterIndex) {
    component.parameters.push({ type: 'text', text: '' });
  }
  component.parameters[slot.parameterIndex] = slot.parameterType === 'text'
    ? { type: 'text', text: value }
    : { type: slot.parameterType, [slot.parameterType]: { link: value } };
  return next;
}

/**
 * Format byte-based limits for human-readable helper text.
 */
export function formatContactZByteLimit(bytes: number): string {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(0)} MB`;
  }
  if (bytes >= 1024) {
    return `${(bytes / 1024).toFixed(0)} KB`;
  }
  return `${bytes} B`;
}

/**
 * Return whether the UI should keep polling while the provider connection is not stable yet.
 */
export function shouldPollContactZConnectionStatus(status: ContactZConnectionStatus): boolean {
  return CONTACT_Z_POLLABLE_CONNECTION_STATUSES.has(status);
}

/**
 * Return whether the channel can start a new WhatsApp pairing request.
 */
export function canConnectContactZConnection(status: ContactZConnectionStatus): boolean {
  return CONTACT_Z_CONNECTABLE_CONNECTION_STATUSES.has(status);
}

/**
 * Return whether the channel can cancel an active connection or pairing request.
 */
export function canDisconnectContactZConnection(status: ContactZConnectionStatus): boolean {
  return CONTACT_Z_DISCONNECTABLE_CONNECTION_STATUSES.has(status);
}
